import React, { useState, useEffect, useRef } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { User, Settings, Globe, DollarSign, Languages, Camera, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from '../auth/SimpleAuthProvider';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const UserProfile = ({ onLogout }) => {
  const { user } = useAuth();
  const { setUserCurrency } = useCurrency();
  const { changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [userSettings, setUserSettings] = useState({
    country: 'US',
    currency: 'USD',
    currencies: [{ code: 'USD', is_primary: true, order: 0 }],
    language: 'en',
    profile_image: null,
    display_name: user?.name || '',
    timezone: 'UTC'
  });
  const [isLoading, setIsLoading] = useState(false);
  const profileRef = useRef(null);
  const fileInputRef = useRef(null);

  // Country options with flags
  const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' }
  ];

  // Currency options
  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' }
  ];

  // Language options
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' }
  ];

  // Get auth headers - uses prosperMindToken as primary, with authToken fallback
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
    if (!token) {
      console.warn('No auth token found in sessionStorage');
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
        setIsSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load user settings on component mount
  useEffect(() => {
    fetchUserSettings();
  }, []);

  const fetchUserSettings = async () => {
    // Get auth headers with fallback
    const authHeaders = getAuthHeaders();
    const axiosAuthHeader = axios.defaults.headers.common['Authorization'];
    
    const finalHeaders = Object.keys(authHeaders).length > 0 
      ? authHeaders
      : (axiosAuthHeader ? { Authorization: axiosAuthHeader } : {});
    
    if (!finalHeaders.Authorization) {
      console.warn('No auth token for fetching profile, using defaults');
      return;
    }
    
    try {
      const fetchResponse = await fetch(`${API}/api/user/profile`, {
        method: 'GET',
        headers: {
          'Authorization': finalHeaders.Authorization
        }
      });
      
      if (!fetchResponse.ok) {
        throw new Error(`Server error: ${fetchResponse.status}`);
      }
      
      const profile = await fetchResponse.json();
      
      console.log('Fetched profile:', profile);
      
      // Ensure currencies array exists
      if (!profile.currencies || profile.currencies.length === 0) {
        profile.currencies = [{ code: profile.currency || 'USD', is_primary: true, order: 0 }];
      }
      
      // Use functional update to avoid stale state issues
      setUserSettings(prevSettings => ({
        ...prevSettings,
        ...profile,
        display_name: profile.display_name || user?.name || prevSettings.display_name || ''
      }));
    } catch (error) {
      console.error('Error fetching user settings:', error);
      // Use defaults and user data - also use functional update
      setUserSettings(prevSettings => ({
        ...prevSettings,
        display_name: user?.name || prevSettings.display_name || '',
        currencies: prevSettings.currencies || [{ code: 'USD', is_primary: true, order: 0 }]
      }));
    }
  };

  // Currency management functions
  const addCurrency = (currencyCode) => {
    const newCurrency = {
      code: currencyCode,
      is_primary: false,
      order: userSettings.currencies.length
    };
    setUserSettings({
      ...userSettings,
      currencies: [...userSettings.currencies, newCurrency]
    });
  };

  const removeCurrency = (index) => {
    const newCurrencies = userSettings.currencies.filter((_, i) => i !== index);
    // Reorder remaining currencies
    const reorderedCurrencies = newCurrencies.map((c, i) => ({ ...c, order: i }));
    setUserSettings({
      ...userSettings,
      currencies: reorderedCurrencies
    });
  };

  const setPrimaryCurrency = (index) => {
    const newCurrencies = userSettings.currencies.map((c, i) => ({
      ...c,
      is_primary: i === index
    }));
    // Update primary currency field for backwards compatibility
    setUserSettings({
      ...userSettings,
      currency: newCurrencies[index].code,
      currencies: newCurrencies
    });
  };

  const moveCurrency = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= userSettings.currencies.length) return;
    
    const newCurrencies = [...userSettings.currencies];
    [newCurrencies[index], newCurrencies[newIndex]] = [newCurrencies[newIndex], newCurrencies[index]];
    
    // Update order values
    const reorderedCurrencies = newCurrencies.map((c, i) => ({ ...c, order: i }));
    setUserSettings({
      ...userSettings,
      currencies: reorderedCurrencies
    });
  };

  const getCurrencyInfo = (code) => currencies.find(c => c.code === code);

  const saveUserSettings = async () => {
    setIsLoading(true);
    
    // Get auth headers with fallback
    const authHeaders = getAuthHeaders();
    const axiosAuthHeader = axios.defaults.headers.common['Authorization'];
    
    const finalHeaders = Object.keys(authHeaders).length > 0 
      ? { ...authHeaders, 'Content-Type': 'application/json' }
      : (axiosAuthHeader ? { Authorization: axiosAuthHeader, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' });
    
    if (!finalHeaders.Authorization) {
      console.error('No authorization token available for profile save!');
      toast.error('Session expired. Please log in again.');
      setIsLoading(false);
      return;
    }
    
    console.log('Saving profile settings:', userSettings);
    console.log('Using auth header:', !!finalHeaders.Authorization);
    console.log('API URL:', `${API}/api/user/profile`);
    
    try {
      // Use fetch API for better reliability
      const fetchResponse = await fetch(`${API}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': finalHeaders.Authorization,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userSettings)
      });
      
      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${fetchResponse.status}`);
      }
      
      const responseData = await fetchResponse.json();
      console.log('Profile save response:', responseData);
      
      if (responseData?.success) {
        // Notify CurrencyProvider about currency change
        if (setUserCurrency) {
          setUserCurrency(userSettings.currency);
        }
        
        // Notify LanguageProvider about language change
        if (changeLanguage) {
          await changeLanguage(userSettings.language);
        }
        
        toast.success(t('messages.profileUpdated') || 'Profile updated successfully!');
        setIsSettingsOpen(false);
      } else {
        console.error('Profile update failed:', responseData);
        toast.error('Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Error saving user settings:', error);
      console.error('Error details:', error.message);
      
      if (error.message?.includes('401')) {
        toast.error('Session expired. Please log in again.');
      } else if (error.message?.includes('Network') || error.message?.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(error.message || t('messages.profileUpdateFailed') || 'Failed to update profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    console.log('handleImageUpload triggered');
    
    const file = event.target.files?.[0];
    
    if (!file) {
      console.log('No file selected, returning');
      return;
    }
    
    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.log('Invalid file type:', file.type);
      toast.error(t('messages.invalidImage') || 'Please select a valid image file');
      return;
    }

    // Validate file size (2MB max for base64 storage)
    if (file.size > 2 * 1024 * 1024) {
      console.log('File too large:', file.size);
      toast.error(t('messages.imageTooLarge') || 'Image must be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('profile_image', file);

    setIsLoading(true);
    
    // Get auth headers
    const authHeaders = getAuthHeaders();
    const axiosAuthHeader = axios.defaults.headers.common['Authorization'];
    
    // Use the most reliable auth header source
    const finalHeaders = Object.keys(authHeaders).length > 0 
      ? authHeaders 
      : (axiosAuthHeader ? { Authorization: axiosAuthHeader } : {});
    
    if (!finalHeaders.Authorization) {
      console.error('No authorization token available!');
      toast.error('Session expired. Please log in again.');
      setIsLoading(false);
      return;
    }
    
    console.log('Starting upload to:', `${API}/api/user/upload-avatar`);
    console.log('API base URL:', API);
    console.log('Auth header present:', !!finalHeaders.Authorization);
    
    try {
      // Use fetch API for file upload (more reliable for multipart/form-data)
      const fetchResponse = await fetch(`${API}/api/user/upload-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': finalHeaders.Authorization
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData
      });
      
      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${fetchResponse.status}`);
      }
      
      const response = { data: await fetchResponse.json() };
      
      console.log('Upload response:', response.data);
      
      if (response.data?.success && response.data?.profile_image_url) {
        // Get clean image URL from response
        const newImageUrl = response.data.profile_image_url;
        
        // Update local state with cache busting for immediate display
        const cacheBustedUrl = `${newImageUrl}?t=${Date.now()}`;
        
        setUserSettings(prevSettings => ({
          ...prevSettings,
          profile_image: cacheBustedUrl
        }));
        
        toast.success('Profile picture uploaded successfully!');
        
        // Refetch profile to ensure consistency
        setTimeout(() => {
          fetchUserSettings();
        }, 500);
      } else {
        console.error('Upload response missing expected data:', response.data);
        toast.error('Upload completed but image URL not received. Please refresh the page.');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        name: error.name
      });
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (error.response?.data?.detail) {
        toast.error(`Upload failed: ${error.response.data.detail}`);
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Network error - could be CORS, timeout, or connection issue
        toast.error('Upload failed: Network error. Please check your connection and try again.');
        console.error('Network error during upload. API URL:', `${API}/api/user/upload-avatar`);
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Please try with a smaller image.');
      } else if (error.message) {
        toast.error(`Upload failed: ${error.message}`);
      } else {
        toast.error('Failed to upload image. Please try again.');
      }
    } finally {
      setIsLoading(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getSelectedCountry = () => countries.find(c => c.code === userSettings.country);
  const getSelectedCurrency = () => currencies.find(c => c.code === userSettings.currency);
  const getSelectedLanguage = () => languages.find(l => l.code === userSettings.language);

  return (
    <div className="relative" ref={profileRef}>
      {/* Profile Trigger Button */}
      <Button
        variant="ghost"
        className="flex items-center gap-2 text-white hover:bg-gray-800 p-2"
        onClick={() => setIsProfileOpen(!isProfileOpen)}
      >
        <Avatar className="h-8 w-8">
          <AvatarImage 
            src={userSettings.profile_image} 
            alt={userSettings.display_name}
          />
          <AvatarFallback className="bg-yellow-500 text-black text-xs font-semibold">
            {getInitials(userSettings.display_name)}
          </AvatarFallback>
        </Avatar>
        <div className="hidden md:flex flex-col items-start">
          <span className="text-sm font-medium">{userSettings.display_name}</span>
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>{getSelectedCountry()?.flag}</span>
            <span>{getSelectedCurrency()?.symbol}</span>
            <span>{getSelectedLanguage()?.flag}</span>
          </div>
        </div>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </Button>

      {/* Profile Dropdown Menu */}
      {isProfileOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage 
                  src={userSettings.profile_image} 
                  alt={userSettings.display_name}
                />
                <AvatarFallback className="bg-yellow-500 text-black font-semibold">
                  {getInitials(userSettings.display_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-white font-medium">{userSettings.display_name}</h3>
                <p className="text-gray-400 text-sm">{user?.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs bg-slate-700 px-2 py-1 rounded">
                    {getSelectedCountry()?.flag} {getSelectedCountry()?.name}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-2">
            <Button
              variant="ghost"
              className="w-full justify-start text-white hover:bg-slate-700"
              onClick={() => {
                setIsSettingsOpen(true);
                setIsProfileOpen(false);
              }}
            >
              <Settings className="h-4 w-4 mr-2" />
              {t('profile.profileSettings')}
            </Button>
            
            <div className="border-t border-slate-700 my-2"></div>
            
            <Button
              variant="ghost"
              className="w-full justify-start text-red-400 hover:bg-red-900/20"
              onClick={onLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('profile.signOut')}
            </Button>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {isSettingsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="h-5 w-5" />
                {t('profile.profileSettings')}
              </CardTitle>
              <CardDescription className="text-slate-300">
                {t('profile.customizeProfile')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Image Section */}
              <div className="space-y-4">
                <Label className="text-white text-base font-medium">{t('profile.profilePicture')}</Label>
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage 
                      src={userSettings.profile_image} 
                      alt={userSettings.display_name}
                    />
                    <AvatarFallback className="bg-yellow-500 text-black text-xl font-semibold">
                      {getInitials(userSettings.display_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-2">
                    <input
                      id="profile-picture-input"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        handleImageUpload(e);
                      }}
                      className="sr-only"
                    />
                    <label
                      htmlFor="profile-picture-input"
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-slate-600 bg-transparent hover:bg-slate-700 h-10 px-4 py-2 text-white cursor-pointer"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      {isLoading ? t('common.uploading') : t('profile.changePicture')}
                    </label>
                    <p className="text-xs text-gray-400">
                      {t('profile.imageFormat')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Display Name */}
              <div className="space-y-2">
                <Label className="text-white">{t('profile.displayName')}</Label>
                <Input
                  value={userSettings.display_name}
                  onChange={(e) => setUserSettings({
                    ...userSettings,
                    display_name: e.target.value
                  })}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Enter your display name"
                />
              </div>

              {/* Country Selection */}
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Country
                </Label>
                <select
                  value={userSettings.country}
                  onChange={(e) => setUserSettings({
                    ...userSettings,
                    country: e.target.value
                  })}
                  className="bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 w-full focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
                >
                  {countries.map((country) => (
                    <option 
                      key={country.code} 
                      value={country.code}
                      className="bg-slate-700 text-white"
                    >
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Multi-Currency Management */}
              <div className="space-y-3">
                <Label className="text-white flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Currency Preferences
                </Label>
                <p className="text-xs text-gray-400">Manage multiple currencies for expenses, investments, and net worth tracking</p>
                
                {/* Currency List */}
                <div className="space-y-2">
                  {userSettings.currencies && userSettings.currencies.map((currency, index) => {
                    const currencyInfo = getCurrencyInfo(currency.code);
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-2 p-3 bg-slate-700 rounded-md border border-slate-600"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{currencyInfo?.symbol}</span>
                            <div>
                              <p className="text-white text-sm font-medium">
                                {currencyInfo?.name} ({currency.code})
                              </p>
                              {currency.is_primary && (
                                <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded">
                                  Primary
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Currency Actions */}
                        <div className="flex items-center gap-1">
                          {/* Move Up */}
                          {index > 0 && (
                            <button
                              onClick={() => moveCurrency(index, 'up')}
                              className="p-1 hover:bg-slate-600 rounded text-gray-400 hover:text-white"
                              title="Move up"
                            >
                              ↑
                            </button>
                          )}
                          
                          {/* Move Down */}
                          {index < userSettings.currencies.length - 1 && (
                            <button
                              onClick={() => moveCurrency(index, 'down')}
                              className="p-1 hover:bg-slate-600 rounded text-gray-400 hover:text-white"
                              title="Move down"
                            >
                              ↓
                            </button>
                          )}
                          
                          {/* Set Primary */}
                          {!currency.is_primary && (
                            <button
                              onClick={() => setPrimaryCurrency(index)}
                              className="px-2 py-1 text-xs hover:bg-slate-600 rounded text-gray-400 hover:text-white"
                              title="Set as primary"
                            >
                              Set Primary
                            </button>
                          )}
                          
                          {/* Remove */}
                          {userSettings.currencies.length > 1 && (
                            <button
                              onClick={() => removeCurrency(index)}
                              className="p-1 hover:bg-red-600 rounded text-gray-400 hover:text-white"
                              title="Remove"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Add Currency */}
                {userSettings.currencies && userSettings.currencies.length < 5 && (
                  <div className="flex gap-2">
                    <select
                      id="add-currency-select"
                      className="flex-1 bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                    >
                      <option value="">Select currency to add...</option>
                      {currencies
                        .filter(c => !userSettings.currencies.find(uc => uc.code === c.code))
                        .map((currency) => (
                          <option key={currency.code} value={currency.code}>
                            {currency.symbol} {currency.name} ({currency.code})
                          </option>
                        ))}
                    </select>
                    <Button
                      type="button"
                      onClick={() => {
                        const select = document.getElementById('add-currency-select');
                        if (select.value) {
                          addCurrency(select.value);
                          select.value = '';
                        }
                      }}
                      className="bg-slate-600 hover:bg-slate-500 text-white"
                    >
                      Add
                    </Button>
                  </div>
                )}
                
                {userSettings.currencies && userSettings.currencies.length >= 5 && (
                  <p className="text-xs text-gray-400 italic">Maximum 5 currencies allowed</p>
                )}
              </div>

              {/* Language Selection */}
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Languages className="h-4 w-4" />
                  Language
                </Label>
                <select
                  value={userSettings.language}
                  onChange={(e) => setUserSettings({
                    ...userSettings,
                    language: e.target.value
                  })}
                  className="bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 w-full focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
                >
                  {languages.map((language) => (
                    <option 
                      key={language.code} 
                      value={language.code}
                      className="bg-slate-700 text-white"
                    >
                      {language.flag} {language.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={saveUserSettings}
                  disabled={isLoading}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {isLoading ? t('common.saving') : t('profile.saveChanges')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsSettingsOpen(false)}
                  className="border-slate-600 text-white hover:bg-slate-700"
                >
                  {t('profile.cancel')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserProfile;